import styles from './index.module.css'

import { LuChevronDown } from '@react-icons/all-files/lu/LuChevronDown'
import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import { LuSparkles } from '@react-icons/all-files/lu/LuSparkles'
import { LuThumbsDown } from '@react-icons/all-files/lu/LuThumbsDown'
import { LuThumbsUp } from '@react-icons/all-files/lu/LuThumbsUp'
import cn from 'clsx'
import groupBy from 'lodash/groupBy'
import uniq from 'lodash/uniq'
import React, { useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { removeNativeTagFromTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import { updateSearchParams } from '@disco/state-management/actions/SearchActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'

import type { NativeCategory, NativeTag, Track } from 'types/track'

import AddTrackTagsModal from 'components/common/add-track-tags-modal'
import { isAiTagPlaceholder } from 'components/common/add-track-tags-modal/ai-tag-placeholder'
import { Button, Flex, Heading, IconButton, Text } from 'components/ds'
import Tag from 'components/ui/tag'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  track: Track
  readOnly?: boolean
}

/**
 * Native tags panel that displays track tags grouped by category inside a
 * collapsible card with an "Add tag" action in its header. Categories are
 * grouped by row without a visible label — the category name stays exposed
 * as an aria-label for accessibility — and the card footer asks for
 * feedback on tag quality.
 * Shows tags with color indicators, handles tag clicks for search, and tag removal
 * Integrates with Mixpanel for analytics and supports read-only modes
 * @param track - Track object containing native tags to display
 * @param readOnly - Whether the pane should be in read-only mode
 */
const NativeTagsPane = (props: Props): null | React.ReactElement<'div'> => {
  const dispatch = useDispatch()
  const isPhone = useSelector(isDeviceTypePhone)
  const tagsByCat = groupBy(props.track.native_tags, 'category_id')
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const businessTagsReadonly: boolean = Boolean(
    useSelector(
      state => state.user.business?.private_metadata.is_native_tags_readonly,
    ),
  )
  const isNativeTagsReadonly = businessTagsReadonly || Boolean(props.readOnly)
  const nativeCategories: {
    [key in NativeCategory['id']]?: NativeCategory
  } = useSelector(state => state.entities.nativeCategories)
  const search = useSelector(state => state.search)
  const [expanded, setExpanded] = useState(true)
  const [addTagOpen, setAddTagOpen] = useState(false)
  // UI-only signal for now — no backend endpoint exists yet to persist
  // tag-accuracy feedback.
  const [vote, setVote] = useState<'up' | 'down' | null>(null)

  const handleTagClick = (tag: NativeTag) => () => {
    const params = {
      nativeTags: uniq(search.nativeTags.concat(tag.id)),
      searchOpen: true,
    }
    mixpanel.sendPureEvent('Clicking on track tag (faceted & track pane)', {
      Tags: [tag.name],
      Type: 'Track pane',
    })
    dispatch(updateSearchParams(params))
  }

  const handleTagRemove = (tag: NativeTag) => () => {
    dispatch(removeNativeTagFromTracks([props.track.id], tag))
  }

  if (isPhone && props.track.native_tags?.length === 0) return null

  const hasTags = (props.track.native_tags?.length ?? 0) > 0
  const showAddTag = !props.readOnly && !isPhone && !props.track?.read_only

  return (
    <div
      className={cn('ExplorerPane_panel', styles.pane)}
      data-testid="native-tags-pane"
    >
      <Flex align="center" justify="between" className={styles.header}>
        <Heading as="h3" size="3" weight="medium">
          Tags
        </Heading>
        <Flex align="center" gap="4">
          {showAddTag && (
            <Button
              variant="surface"
              color="gray"
              size="1"
              radius="full"
              disabled={businessTagsReadonly}
              title={
                businessTagsReadonly
                  ? 'Tag editing is not enabled for this DISCO.'
                  : undefined
              }
              onClick={() => setAddTagOpen(true)}
            >
              <LuPlus size={14} />
              Add tag
            </Button>
          )}
          <IconButton
            variant="ghost"
            color="gray"
            size="1"
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse tags' : 'Expand tags'}
            onClick={() => setExpanded(value => !value)}
          >
            <LuChevronDown
              size={18}
              className={cn(styles.chevron, {
                [styles.chevronUp]: expanded,
              })}
            />
          </IconButton>
        </Flex>
      </Flex>
      {expanded && hasTags && (
        <>
          <div className={styles.body}>
            {Object.keys(tagsByCat)
              .map(Number)
              .map((id: NativeCategory['id']) => {
                const categoryName = nativeCategories[id]?.name
                return (
                  <div
                    key={id}
                    className={styles.categoryRow}
                    role="group"
                    aria-label={categoryName ?? 'Tag category'}
                    data-testid="native-tags-category"
                  >
                    {(tagsByCat[id] ?? []).map((tag: NativeTag) => (
                      <span className={styles.tag} key={tag.id}>
                        <Tag
                          tag={tag.name}
                          tagColor={
                            colorIndicatorsEnabled ? tag.color : undefined
                          }
                          ai={isAiTagPlaceholder(tag)}
                          variant="surface"
                          onTagClick={isPhone ? undefined : handleTagClick(tag)}
                          onRemoveClick={
                            isNativeTagsReadonly ||
                            isPhone ||
                            props.track?.read_only
                              ? undefined
                              : handleTagRemove(tag)
                          }
                        />
                      </span>
                    ))}
                  </div>
                )
              })}
          </div>
          <Flex align="center" justify="between" className={styles.footer}>
            <Flex align="center" gap="2">
              <LuSparkles size={14} className={styles.footerSparkle} />
              <Text size="1" color="gray">
                Were these tags helpful?
              </Text>
            </Flex>
            <Flex align="center" gap="3">
              <IconButton
                variant={vote === 'up' ? 'soft' : 'ghost'}
                color={vote === 'up' ? 'green' : 'gray'}
                size="1"
                aria-label="Tags were helpful"
                aria-pressed={vote === 'up'}
                onClick={() => setVote(value => (value === 'up' ? null : 'up'))}
              >
                <LuThumbsUp size={14} />
              </IconButton>
              <IconButton
                variant={vote === 'down' ? 'soft' : 'ghost'}
                color={vote === 'down' ? 'red' : 'gray'}
                size="1"
                aria-label="Tags were not helpful"
                aria-pressed={vote === 'down'}
                onClick={() =>
                  setVote(value => (value === 'down' ? null : 'down'))
                }
              >
                <LuThumbsDown size={14} />
              </IconButton>
            </Flex>
          </Flex>
        </>
      )}
      {addTagOpen && (
        <AddTrackTagsModal
          trackIds={[props.track.id]}
          assignedTags={props.track.native_tags || []}
          onClose={() => setAddTagOpen(false)}
        />
      )}
    </div>
  )
}

export default NativeTagsPane
